import { useRef, useState, useEffect } from 'react';
import { Drawer, useMediaQuery, useTheme } from '@mui/material';
import AddAccount from '../../pages/dashboard/payments/AddAccount';
import { WalletBalancesSkeleton } from './WalletBalancesSkeleton';
import WalletBalanceItem from './WalletBalanceItem';
import { Wallet } from '../../interfaces/Wallet';
import { useUserProfile } from '../../customhooks/useUserProfile';
import { hasPermission } from '../../pages/auth';
import { SuitUIButton } from '../SuitUIButton';
import { AddCircleIcon, ChevronLeftIcon, ChevronRightIcon } from '../icons';
import CustomModal from '../CustomModal';
import { G3PromptDialog } from '../../pages/dashboard/payments/G3PromptDialog';
import useCurrencies from '../../customhooks/useCurrencies';
import CreateAccountPrompt from './CreateAccountPrompt';

interface WalletAction {
    onHandleClick: () => void;
    display: boolean;
    title: string;
    icon: JSX.Element;
}

export const WalletBalances = (props: {
    wallets: Wallet[];
    isLoading: boolean;
    actions?: WalletAction[];
    setShowPrompt?: (val: boolean) => void;
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [showPrompt, setShowPrompt] = useState(false);

    const { currencies } = useCurrencies({
        all: false
    });

    const { wallets, isLoading } = props;
    const { isLoading: isLoadingUserProfile, permissions } = useUserProfile();

    const ref = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const [currencyCode, setCurrencyCode] = useState('');
    const [showG3Modal, setShowG3Modal] = useState(false);
    const [showAddAccount, setShowAddAccount] = useState(false);

    const updateScrollState = () => {
        if (ref.current) {
            setCanScrollLeft(ref.current.scrollLeft > 0);
            setCanScrollRight(
                ref.current.scrollLeft + ref.current.clientWidth <
                    ref.current.scrollWidth
            );
        }
    };

    useEffect(() => {
        const scrollElement = ref.current;
        updateScrollState();

        if (scrollElement) {
            scrollElement.addEventListener('scroll', updateScrollState);
            return () =>
                scrollElement.removeEventListener('scroll', updateScrollState);
        }
    }, [wallets, isLoading]);

    const handleNav = (direction: 'left' | 'right') => {
        if (ref.current) {
            ref.current.scrollBy({
                left: direction === 'left' ? -400 : 400,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        if (isLoading || !wallets?.length) return;

        const nairaWallet = wallets?.find(
            (item) => item.currency === 'NGN' && item.walletReference?.length
        );

        if (!nairaWallet) {
            setShowPrompt(true);
        }
    }, [wallets, isLoading]);

    return (
        <div>
            {permissions &&
                hasPermission({
                    userPermissions: permissions,
                    module: 'payments',
                    permissionList: ['initiate']
                }) &&
                props.actions?.length && (
                    <div className='hidden lg:flex gap-4 w-full items-right justify-end pr-[52px] mt-[-46px] mb-6'>
                        {props.actions?.map((item) => (
                            <button
                                key={item.title}
                                className='flex items-center gap-2 text-semibold text-gray200'
                                onClick={item?.onHandleClick}
                            >
                                {item.icon}
                                {item?.title}
                            </button>
                        ))}
                    </div>
                )}

            <div className='flex gap-[12px] overflow-auto' ref={ref}>
                {isLoading ? (
                    <WalletBalancesSkeleton length={6} />
                ) : (
                    <>
                        {currencies.filter(({ currencyCode }) => {
                            return !wallets.some(
                                (wallet) => wallet.currency === currencyCode
                            );
                        })?.length > 0 && (
                            <div
                                onClick={() => setShowAddAccount(true)}
                                className='flex flex-col gap-4 items-left justify-center cursor-pointer bg-white min-w-[208px] px-6 py-5 rounded-[24px] border border-appcolorfaintnine'
                            >
                                <AddCircleIcon />
                                <p className='text-[#344054] text-[18px] leading-[28px] font-semibold'>
                                    Add new wallet
                                </p>
                            </div>
                        )}

                        {[...wallets].map((item) => (
                            <div key={item.corporateId}>
                                <WalletBalanceItem item={item} />
                            </div>
                        ))}
                    </>
                )}
            </div>

            <div className='flex justify-end gap-4 my-4'>
                <button
                    onClick={() => handleNav('left')}
                    disabled={!canScrollLeft}
                    className={`flex items-center justify-center w-[36px] h-[36px] rounded-[18px] ${
                        canScrollLeft
                            ? 'bg-black text-white'
                            : 'bg-white text-gray-500 cursor-not-allowed'
                    }`}
                >
                    <ChevronLeftIcon />
                </button>

                <button
                    onClick={() => handleNav('right')}
                    disabled={!canScrollRight}
                    className={`flex items-center justify-center w-[36px] h-[36px] rounded-[18px] ${
                        canScrollRight
                            ? 'bg-black text-white'
                            : 'bg-white text-gray-500 cursor-not-allowed'
                    }`}
                >
                    <ChevronRightIcon />
                </button>
            </div>

            {props.actions?.length &&
                permissions &&
                hasPermission({
                    userPermissions: permissions,
                    module: 'payments',
                    permissionList: ['initiate']
                }) && (
                    <div className='mb-6 lg:hidden grid grid-cols-2 gap-2'>
                        {props.actions?.map((item, index) => (
                            <SuitUIButton
                                key={index}
                                text={item.title}
                                onClick={item.onHandleClick}
                                invert
                                fullWidth
                                iconLeft={item.icon}
                                style={`border border-gray300 text-steelBlue font-medium text-sm leading-[20px] ${
                                    props.actions?.length === 3 && index === 2
                                        ? 'col-span-2'
                                        : ''
                                }`}
                            />
                        ))}
                    </div>
                )}

            <CustomModal
                maxWidth='lg'
                isModalOpen={showG3Modal}
                onClose={() => setShowG3Modal(false)}
            >
                <G3PromptDialog
                    currencyCode={currencyCode}
                    setShowG3Modal={setShowG3Modal}
                />
            </CustomModal>

            <Drawer
                anchor={isMobile ? 'bottom' : 'right'}
                open={showAddAccount}
                onClose={() => setShowAddAccount(false)}
                sx={{
                    '.MuiDrawer-paperAnchorRight': {
                        width: {
                            xs: '95%',
                            lg: '33rem'
                        }
                    }
                }}
                PaperProps={{
                    sx: {
                        py: 2,
                        px: 4
                    }
                }}
            >
                <AddAccount
                    currencyCode={currencyCode}
                    setCurrencyCode={setCurrencyCode}
                    setShowG3Modal={setShowG3Modal}
                    setShowAddAccount={setShowAddAccount}
                />
            </Drawer>

            <CreateAccountPrompt
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
            />
        </div>
    );
};
